'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

// Base schema from integrations.ts
const klarnaCredentialsSchema = z.object({
  apiKey: z.string().min(1).trim(),
  sandboxed: z.boolean(),
  location: z.enum(['North America', 'Europe', 'Oceania']),
})

const klarnaFormSchema = klarnaCredentialsSchema
  .refine(
    ({ sandboxed, apiKey }) => {
      if (!sandboxed && apiKey.includes('test')) return false
      return true
    },
    { message: 'The api key should be a live version' }
  )

type FormValues = z.infer<typeof klarnaFormSchema>

interface KlarnaFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function KlarnaForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: KlarnaFormProps) {
  const { t } = useTranslation('integrations')

  // Check if existing API key exists (show bullets if so)
  const hasExistingApiKey = defaultValues.credentials?.apiKey && defaultValues.credentials.apiKey.length > 0

  const form = useForm<FormValues>({
    mode: 'all',
    resolver: zodResolver(klarnaFormSchema),
    defaultValues: {
      sandboxed: defaultValues.credentials?.sandboxed ?? false,
      apiKey: hasExistingApiKey ? '•'.repeat(20) : '',
      location: defaultValues.credentials?.location ?? 'North America',
    },
  })

  const { register, handleSubmit, formState: { errors, touchedFields }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const credentials = data

    // Only include apiKey in credentials if it was touched (changed)
    const submitData: any = {
      credentials: {
        sandboxed: credentials.sandboxed,
        location: credentials.location,
      },
    }

    // Only update API key if the field was touched (user edited it)
    if (touchedFields.apiKey) {
      submitData.credentials.apiKey = credentials.apiKey
    } else if (hasExistingApiKey) {
      // Keep existing API key if not touched
      submitData.credentials.apiKey = defaultValues.credentials.apiKey
    }

    await onSubmit(submitData)
  }

  const sandboxed = watch('sandboxed')
  const apiKey = watch('apiKey')

  // Show warning if trying to use test key in production
  const showTestKeyWarning = !sandboxed && apiKey.toLowerCase().includes('test')

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Environment Toggle */}
      <Card>
        <CardHeader>
          <CardTitle>{t('klarna.environmentTitle')}</CardTitle>
          <CardDescription>{t('klarna.environmentDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="sandboxed" className="font-medium">
                {t('klarna.testEnvironment')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('klarna.testEnvironmentHint')}
              </p>
            </div>
            <Switch
              id="sandboxed"
              checked={sandboxed}
              onCheckedChange={(checked) => setValue('sandboxed', checked)}
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      {/* API Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>{t('klarna.apiCredentials')}</CardTitle>
          <CardDescription>{t('klarna.apiCredentialsDescription', { mode: sandboxed ? t('forms.common.test') : t('forms.common.live') })}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="apiKey">
              {t('klarna.apiKey')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="apiKey"
              type="password"
              placeholder={t('klarna.apiKeyPlaceholder')}
              disabled={isLoading}
              {...register('apiKey')}
            />
            {touchedFields.apiKey && errors.apiKey && (
              <p className="text-xs text-destructive">{t('klarna.apiKeyRequired')}</p>
            )}
          </div>

          {showTestKeyWarning && touchedFields.apiKey && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t('klarna.invalidApiKeyTitle')}</AlertTitle>
              <AlertDescription>
                {t('klarna.invalidApiKeyDescription')}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Region Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('klarna.apiRegion')}</CardTitle>
          <CardDescription>{t('klarna.apiRegionDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            id="location"
            value={watch('location')}
            onValueChange={(value) => setValue('location', value as 'North America' | 'Europe' | 'Oceania')}
            disabled={isLoading}
            className="space-y-3"
          >
            {(['North America', 'Europe', 'Oceania'] as const).map((location) => (
              <div key={location} className="flex items-center space-x-2">
                <RadioGroupItem value={location} id={location.toLowerCase().replace(' ', '-')} />
                <Label htmlFor={location.toLowerCase().replace(' ', '-')} className="cursor-pointer font-normal">
                  {location}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('klarna.setupInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              <li>{t('klarna.setupStep1')}</li>
              <li>{t('klarna.setupStep2')}</li>
              <li dangerouslySetInnerHTML={{ __html: t('klarna.setupStep3') }} />
              <li>{t('klarna.setupStep4')}</li>
              <li>{t('klarna.setupStep5')}</li>
              <li dangerouslySetInnerHTML={{ __html: t('klarna.setupStep6') }} />
            </ol>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
